import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Activity, Link as LinkIcon, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from "lucide-react";

export default function PlatformHealth() {
  const { data: connectors, isLoading: connLoading, refetch } = trpc.admin.dashboard.connectors.useQuery();
  const { data: stats, isLoading: statsLoading } = trpc.admin.dashboard.stats.useQuery();
  const { data: reporting, isLoading: reportingLoading } = trpc.admin.dashboard.reporting.useQuery();

  const allConnectorsOk = connectors?.every((c) => c.status === "SUCCESS") ?? false;
  const anyConnectorFailed = connectors?.some((c) => c.status === "FAILED") ?? false;

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Santé plateforme</h1>
            <p className="text-muted-foreground">Supervision des connecteurs, jobs et métriques opérationnelles</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Global health indicator */}
        <Card className={`${anyConnectorFailed ? "border-destructive/30" : allConnectorsOk ? "border-success/30" : "border-warning/30"}`}>
          <CardContent className="flex items-center gap-4 p-6">
            {anyConnectorFailed ? (
              <XCircle className="h-8 w-8 text-destructive" />
            ) : allConnectorsOk ? (
              <CheckCircle2 className="h-8 w-8 text-success" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-warning" />
            )}
            <div>
              <div className="font-semibold text-lg">
                {anyConnectorFailed ? "Anomalie détectée" : allConnectorsOk ? "Plateforme opérationnelle" : "Statut inconnu"}
              </div>
              <div className="text-sm text-muted-foreground">
                {connectors?.length ?? 0} connecteur(s) surveillé(s)
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métriques opérationnelles */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Métriques opérationnelles
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AO à qualifier</CardTitle>
                <Activity className="h-4 w-4 text-warning" />
              </CardHeader>
              <CardContent>
                {statsLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className={`text-2xl font-bold ${(stats?.tendersToQualify ?? 0) > 0 ? "text-warning" : ""}`}>
                      {stats?.tendersToQualify ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">en attente de qualification</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Projets en retard</CardTitle>
                <XCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                {statsLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className={`text-2xl font-bold ${(stats?.projectsLate ?? 0) > 0 ? "text-destructive" : ""}`}>
                      {stats?.projectsLate ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">deadline dépassée</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sans partenaire</CardTitle>
                <AlertTriangle className="h-4 w-4 text-warning" />
              </CardHeader>
              <CardContent>
                {statsLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className={`text-2xl font-bold ${(stats?.projectsWithoutPartner ?? 0) > 0 ? "text-warning" : ""}`}>
                      {stats?.projectsWithoutPartner ?? 0}
                    </div>
                    <p className="text-xs text-muted-foreground">projets à assigner</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Délai prod moyen</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {reportingLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className="text-2xl font-bold">{reporting?.avgProductionDays ?? 0}j</div>
                    <p className="text-xs text-muted-foreground">création → soumission</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Connecteurs */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Connecteurs d'ingestion
          </h2>
          <div className="space-y-3">
            {connLoading ? (
              Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
            ) : !connectors || connectors.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  Aucun connecteur configuré
                </CardContent>
              </Card>
            ) : (
              connectors.map((c) => (
                <Card key={c.name} className={c.status === "FAILED" ? "border-destructive/30" : c.status === "SUCCESS" ? "border-success/30" : ""}>
                  <CardContent className="flex items-center justify-between p-5">
                    <div className="flex items-center gap-4">
                      {c.status === "SUCCESS" ? (
                        <CheckCircle2 className="h-6 w-6 text-success" />
                      ) : c.status === "FAILED" ? (
                        <XCircle className="h-6 w-6 text-destructive" />
                      ) : (
                        <AlertTriangle className="h-6 w-6 text-muted-foreground" />
                      )}
                      <div>
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Dernier run : {c.lastRunAt ? formatDate(c.lastRunAt) : "Jamais"}
                          {(c.errorsCount ?? 0) > 0 && (
                            <span className="text-destructive ml-2">· {c.errorsCount} erreur(s)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant={c.status === "SUCCESS" ? "success" : c.status === "FAILED" ? "destructive" : "secondary"}>
                      {c.status ?? "N/A"}
                    </Badge>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Répartition projets */}
        {reporting && (
          <div>
            <h2 className="text-base font-semibold mb-3">Répartition des projets par statut</h2>
            <Card>
              <CardContent className="p-6">
                <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4">
                  {Object.entries(reporting.statusDistribution)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm text-muted-foreground">{status}</span>
                        <span className="font-semibold">{count as number}</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
